import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { User, UserRole, Company } from 'shared/types';
import authService from 'shared/services/authService';
import { setCompanyContext, clearCache as clearApiClientCache } from 'shared/services/apiClient';
import { userApi } from 'shared/services/userApi';
import { companyApi } from 'shared/services/companyApi';
import { trackingService } from 'shared/services/trackingService';
import { userVerificationsApi } from 'shared/services/userVerificationsApi';
import { PendingVerificationsCount, calculatePendingVerifications } from 'shared/utils/verificationUtils';
import { UserCompanyVerifications } from 'shared/services/userVerificationsApi';
import { PermissionsProvider, usePermissions } from './PermissionsContext';
import { queryClient } from 'shared/hooks/queryConfig';

// Re-export for backwards compatibility
export type { PermissionsState, PersistedPermissions } from './PermissionsContext';

interface LoginResponseActiveCompany {
  _id: string;
  name: string;
  ruc?: string;
  taxId?: string;
  address?: string;
  phone?: string;
  email?: string;
  industry?: string;
  userCount?: number;
  status?: 'active' | 'inactive' | 'suspended';
  settings?: { modules?: { workPermits?: boolean } };
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

interface LoginResponseCompanyEntry {
  company?: LoginResponseActiveCompany | string;
  role?: string;
  isActive?: boolean;
  /** Provider admin lent into this client: manages its work documents. */
  canManageWorkDocuments?: boolean;
}

interface LoginResponseUser {
  id: string;
  _id?: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  phone?: string;
  cedula?: string;
  role?: string;
  globalRole?: string;
  globalRoles?: string[];
  companies?: LoginResponseCompanyEntry[];
  activeCompany?: LoginResponseActiveCompany;
  supervisedCompanies?: User['supervisedCompanies'];
  manages?: User['manages'];
  departments?: User['departments'];
  isActive?: boolean;
  active?: boolean;
  status?: string;
  acceptedTerms?: boolean;
  acceptedPrivacyPolicy?: boolean;
  termsAcceptedAt?: string;
  privacyPolicyAcceptedAt?: string;
  profile?: User['profile'];
  verificationSummary?: User['verificationSummary'];
  reviewSummary?: User['reviewSummary'];
  companiesVerifications?: UserCompanyVerifications[];
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  talentLMSUserId?: string;
  __v?: number;
  isSuperSuperAdmin?: boolean;
}

interface DirectLoginParsedResponse {
  token: string;
  data?: LoginResponseUser;
  user?: LoginResponseUser;
}

interface TalentLMSUserData {
  id: string;
  login: string;
  email: string;
  firstName: string;
  lastName: string;
  courses?: any[];
}

/**
 * AuthContextType — full interface for backwards compatibility.
 * All consumers use useAuth() which returns this shape.
 * Internally, values come from AuthCoreContext + PermissionsContext.
 */
interface AuthContextType {
  // Core auth
  user: User | null;
  company: Company | null;
  userAvatarUrl: string | null;
  login: (email: string, password: string) => Promise<User>;
  completeLoginWithToken: () => Promise<User>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  sessionExpired: boolean;
  triggerSessionExpired: () => void;
  clearSessionExpired: () => void;
  refreshUserAvatar: () => Promise<void>;
  /** Merge partial fields into the current user (state + localStorage)
   *  so UI surfaces tied to AuthContext reflect a self-edit instantly. */
  updateUserLocal: (patch: Partial<User>) => void;
  /** Switch the active company for multi-company users. Updates user
   *  state + localStorage, recomputes the effective role from the
   *  target company's entry, calls setCompanyContext to clear cached
   *  query data, and resolves once the swap is reflected in state. */
  switchCompany: (companyId: string) => Promise<void>;
  /** Switch the active platform role for identities holding more than one
   *  (super_admin <-> safety_staff). Calls the BE, stores the fresh JWT,
   *  updates user.role/globalRole, and clears the query cache since the data
   *  scope changes (super_admin sees all; safety_staff sees their portfolio). */
  switchGlobalRole: (role: string) => Promise<void>;
  pendingVerifications: PendingVerificationsCount | null;
  refreshPendingVerifications: () => Promise<void>;
  talentLMSUser: TalentLMSUserData | null;
  talentLMSCourses: any[] | null;
  refreshTalentLMSData: () => Promise<void>;
  // Permissions (delegated to PermissionsContext)
  permissions: import('./PermissionsContext').PermissionsState | null;
  setPermissions: (permissions: import('./PermissionsContext').PermissionsState) => void;
  showPermissionsCheck: boolean;
  setShowPermissionsCheck: (show: boolean) => void;
  savePermissionsToStorage: (permissions: import('./PermissionsContext').PermissionsState) => void;
}

/**
 * Strip PII from the User object before writing to `localStorage`. The
 * persisted blob is only consulted to decide whether to attempt a session
 * resume on app boot — the real document is re-fetched via `/auth/me`
 * immediately after — so any field beyond the shell-rendering essentials
 * is dead weight that widens the XSS / extension / shared-device blast
 * radius. Costa Rican cedula is regulated PII under Ley 8968 and must not
 * sit at rest in localStorage.
 */
const toPersistableUser = (u: User): Partial<User> => ({
  id: u.id,
  _id: u._id,
  email: u.email,
  firstName: u.firstName,
  lastName: u.lastName,
  fullName: u.fullName,
  name: u.name,
  role: u.role,
  company: u.company ? ({ _id: u.company._id, name: u.company.name } as User['company']) : undefined,
  companyId: u.companyId,
  // `companies[]` is needed for the company-switcher in ProfileDrawer, but
  // we only keep id/name/role — drop nested PII (member arrays, settings,
  // verifications) that may be populated on the BE response.
  // Intentionally a sparse projection — only the fields used by the
  // ProfileDrawer company-switcher. Cast through `unknown` because the
  // canonical `User.companies[]` shape requires several non-PII admin
  // fields (`startDate`, `permissions`) that we don't need offline;
  // they'll be replaced by `/auth/me` on next boot.
  companies: (u.companies || []).map((c) => {
    const populated = (c as unknown as { company?: { _id?: string; name?: string } | string }).company;
    const companyObj = populated && typeof populated === 'object' ? populated : null;
    return {
      companyId: c.companyId || (typeof populated === 'string' ? populated : companyObj?._id),
      companyName: c.companyName || companyObj?.name,
      role: c.role,
      isActive: c.isActive,
    };
  }) as unknown as User['companies'],
  manages: u.manages,
  isActive: u.isActive,
  active: u.active,
  status: u.status,
});

/**
 * Strip non-essentials from the Company object before localStorage. We keep
 * only what the shell uses for offline render: id + name + module flags.
 * The full document is re-fetched on boot.
 */
const toPersistableCompany = (c: Company): Partial<Company> => ({
  _id: c._id,
  id: c.id,
  name: c.name,
  settings: c.settings ? ({ modules: c.settings.modules } as Company['settings']) : undefined,
});

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ==================== CORE AUTH PROVIDER ====================

interface AuthCoreProviderProps {
  children: ReactNode;
}

const AuthCoreProvider: React.FC<AuthCoreProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [userAvatarUrl, setUserAvatarUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingVerifications, setPendingVerifications] = useState<PendingVerificationsCount | null>(null);
  const [talentLMSUser, setTalentLMSUser] = useState<TalentLMSUserData | null>(null);
  const [talentLMSCourses, setTalentLMSCourses] = useState<any[] | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Read from PermissionsContext (wraps us from above in AuthProvider)
  const permissionsCtx = usePermissions();

  // ==================== INITIALIZATION ====================

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const token = authService.getStoredToken();

        if (storedUser && token) {
          try {
            const response = await authService.getCurrentUser(token);
            const freshUserData = (response.data || response.user) as unknown as LoginResponseUser | undefined;
            if (!freshUserData) throw new Error('Respuesta de usuario inválida');
            await processLoginResponse(freshUserData, token);
          } catch {
            localStorage.removeItem('user');
            localStorage.removeItem('company');
            authService.removeToken();
            setUser(null);
            setCompany(null);
            setCompanyContext(null);
          }
        } else {
          localStorage.removeItem('user');
          localStorage.removeItem('company');
          authService.removeToken();
          setUser(null);
          setCompany(null);
          setCompanyContext(null);
        }
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('company');
        authService.removeToken();
        setUser(null);
        setCompany(null);
        setCompanyContext(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defensive cache wipe on company-context change. Most query-key factories
  // don't include `companyId` as a discriminator (see queryConfig.ts), so a
  // mid-session company switch would otherwise surface the previous tenant's
  // cached lists for up to `staleTime` (5 min). Today only login/logout flip
  // the company context; this listener also future-proofs any switchCompany
  // action that may be added later.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ previous: string | null; current: string | null }>).detail;
      // Any context change must clear cache, including the null → companyId
      // transition that fires immediately after a platform user navigates
      // into /companies/:id/* for the first time. The previous guard
      // (`detail?.previous && ...`) skipped that transition because `null`
      // is falsy, leaving global cached responses keyed under tenant-less
      // keys to be served when a tenant context was later applied.
      if (detail && detail.previous !== detail.current) {
        queryClient.clear();
      }
    };
    window.addEventListener('company-context-changed', handler);
    return () => window.removeEventListener('company-context-changed', handler);
  }, []);

  // ==================== USER DATA LOADERS ====================

  const loadUserAvatar = async (_userData: User) => {
    try {
      const response = await userApi.getProfilePictureDownloadUrl();
      if (response.success && response.data?.url) {
        setUserAvatarUrl(response.data.url);
      } else {
        setUserAvatarUrl(null);
      }
    } catch {
      setUserAvatarUrl(null);
    }
  };

  const refreshUserAvatar = useCallback(async () => {
    if (user) await loadUserAvatar(user);
   
  }, [user]);

  const loadPendingVerifications = async (userData: User) => {
    // Roles fully exempt from the verification system: platform admins
    // Per-company verifications only apply to client_staff
    // (Colaboradores). Everyone else — platform admins, approvers,
    // security, AND company_admin — is exempt, so we skip the fetch
    // entirely. This also stops the daily "Pendientes de Verificación"
    // popup from firing on roles that have nothing to upload.
    if (userData.role !== UserRole.CLIENT_STAFF) {
      setPendingVerifications(null);
      return;
    }
    try {
      const verifications = await userVerificationsApi.getMyVerifications();
      // getMyVerifications() returns every company the user belongs to —
      // a client_staff on loan to more than one company (external/lent-in
      // via a provider) would otherwise see pending docs bundled from
      // companies they aren't even currently logged in as. Scope to the
      // active company only, matching what's actually visible right now.
      const activeCompanyId = userData.company?._id;
      const scoped = activeCompanyId
        ? verifications.filter((v) => v.company.id === activeCompanyId)
        : verifications;
      const pendingCount = calculatePendingVerifications(scoped);
      setPendingVerifications(pendingCount);
    } catch {
      setPendingVerifications(null);
    }
  };

  const refreshPendingVerifications = useCallback(async () => {
    if (user) await loadPendingVerifications(user);
   
  }, [user]);

  const loadTalentLMSData = async (_userData: User) => {
    setTalentLMSUser(null);
    setTalentLMSCourses(null);
  };

  const refreshTalentLMSData = useCallback(async () => {
    if (user) await loadTalentLMSData(user);
   
  }, [user]);

  // ==================== LOGIN PROCESSING ====================

  const processLoginResponse = async (responseUser: LoginResponseUser, token: string): Promise<User> => {
    authService.setToken(token);

    const rawCompanyData = responseUser.activeCompany ||
      (responseUser.companies?.[0]?.company);
    const activeCompanyData = typeof rawCompanyData === 'string' ? undefined : rawCompanyData;
    const activeCompanyId = activeCompanyData?._id?.toString();

    const deriveEffectiveRole = (): UserRole => {
      if (responseUser.globalRole) return responseUser.globalRole as UserRole;
      if (responseUser.companies && responseUser.companies.length > 0) {
        if (activeCompanyId) {
          const matchingEntry = responseUser.companies.find(
            (c: LoginResponseCompanyEntry) => (typeof c.company === 'string' ? c.company : c.company?._id)?.toString() === activeCompanyId
          );
          if (matchingEntry?.role) return matchingEntry.role as UserRole;
        }
        const firstActive = responseUser.companies.find((c: LoginResponseCompanyEntry) => c.isActive !== false);
        if (firstActive?.role) return firstActive.role as UserRole;
      }
      if (responseUser.role) return responseUser.role as UserRole;
      return UserRole.CLIENT_STAFF;
    };

    const effectiveRole = deriveEffectiveRole();

    // Capacidad (no rol) del admin de una proveedora prestado a un cliente:
    // gestiona las Fichas/Evaluaciones/Permisos de la empresa ACTIVA. Se
    // deriva de la entrada de esa empresa, nunca de la primera del array.
    const activeEntry = activeCompanyId
      ? responseUser.companies?.find(
          (c: LoginResponseCompanyEntry) =>
            (typeof c.company === 'string' ? c.company : c.company?._id)?.toString() === activeCompanyId,
        )
      : undefined;
    const canManageWorkDocuments = !!activeEntry?.canManageWorkDocuments;

    const newUser: User = {
      id: responseUser.id,
      _id: responseUser._id || responseUser.id,
      email: responseUser.email,
      firstName: responseUser.firstName,
      lastName: responseUser.lastName,
      fullName: responseUser.fullName || `${responseUser.firstName} ${responseUser.lastName}`.trim(),
      name: `${responseUser.firstName} ${responseUser.lastName}`.trim(),
      phone: responseUser.phone,
      cedula: responseUser.cedula,
      role: effectiveRole,
      canManageWorkDocuments,
      globalRole: responseUser.globalRole as UserRole | undefined,
      globalRoles: responseUser.globalRoles as UserRole[] | undefined,
      company: activeCompanyData as User['company'],
      companyId: activeCompanyData?._id,
      companies: responseUser.companies as User['companies'],
      supervisedCompanies: responseUser.supervisedCompanies,
      manages: responseUser.manages,
      departments: responseUser.departments,
      isActive: responseUser.isActive ?? responseUser.active ?? true,
      active: responseUser.active ?? responseUser.isActive ?? true,
      status: (responseUser.status as User['status']) || (responseUser.isActive ? 'active' : 'deactivated'),
      acceptedTerms: responseUser.acceptedTerms ?? true,
      acceptedPrivacyPolicy: responseUser.acceptedPrivacyPolicy ?? true,
      termsAcceptedAt: responseUser.termsAcceptedAt || new Date().toISOString(),
      privacyPolicyAcceptedAt: responseUser.privacyPolicyAcceptedAt || new Date().toISOString(),
      profile: responseUser.profile,
      verificationSummary: responseUser.verificationSummary,
      reviewSummary: responseUser.reviewSummary,
      createdAt: responseUser.createdAt || new Date().toISOString(),
      updatedAt: responseUser.updatedAt || new Date().toISOString(),
      lastLogin: responseUser.lastLogin,
      talentLMSUserId: responseUser.talentLMSUserId,
      __v: responseUser.__v,
      isSuperSuperAdmin: responseUser.isSuperSuperAdmin
    };

    if (!newUser.isActive && newUser.status === 'deactivated') {
      throw new Error('Tu cuenta ha sido desactivada. Contacta al administrador para más información.');
    }

    let companyData: Company | null = null;
    if (activeCompanyData) {
      companyData = {
        _id: activeCompanyData._id,
        id: activeCompanyData._id,
        name: activeCompanyData.name,
        taxId: activeCompanyData.ruc || activeCompanyData.taxId,
        address: activeCompanyData.address || '',
        phone: activeCompanyData.phone || '',
        email: activeCompanyData.email || '',
        industry: activeCompanyData.industry || 'Construcción',
        userCount: activeCompanyData.userCount || 0,
        contactPerson: {
          name: `${responseUser.firstName} ${responseUser.lastName}`.trim(),
          position: 'Administrador',
          email: responseUser.email,
          phone: activeCompanyData.phone || ''
        },
        legalRepresentative: {
          name: `${responseUser.firstName} ${responseUser.lastName}`.trim(),
          cedula: '',
          position: 'Representante Legal'
        },
        status: (activeCompanyData.status as Company['status']) || 'active',
        settings: activeCompanyData.settings as any,
        createdAt: activeCompanyData.createdAt || new Date(),
        updatedAt: activeCompanyData.updatedAt || new Date()
      };
    }

    setUser(newUser);
    setCompany(companyData);
    // Tell the API client which company to scope subsequent requests by.
    // This populates the `X-Company-Context` header — without it multi-company
    // users silently fall back to whichever company the BE picks first.
    setCompanyContext(activeCompanyId || null);
    // Persist only the minimum fields needed for a warm reload to show the
    // shell (sidebar, avatar, role-gated routes) while `/auth/me` re-fetches
    // the full document on app boot. Do NOT persist `cedula`, `phone`,
    // `companies[]`, `departments[]`, `verificationSummary`, `reviewSummary`,
    // `profile`, etc. — those are PII and have no use offline. Stripping
    // them shrinks the blast radius of XSS / extension / shared-device
    // localStorage reads.
    localStorage.setItem('user', JSON.stringify(toPersistableUser(newUser)));
    if (companyData) {
      localStorage.setItem('company', JSON.stringify(toPersistableCompany(companyData)));
    } else {
      localStorage.removeItem('company');
    }

    loadUserAvatar(newUser);

    // Inlined `companiesVerifications` from the login response only
    // applies to client_staff (Colaboradores) — for any other role we
    // skip the count entirely so the daily popup + profile alert
    // don't fire on roles that have nothing to upload.
    if (newUser.role !== UserRole.CLIENT_STAFF) {
      setPendingVerifications(null);
    } else if (responseUser.companiesVerifications) {
      // Same active-company scoping as loadPendingVerifications below —
      // the login response inlines every company's verifications, but the
      // popup should only reflect the company just logged into.
      const scoped = activeCompanyId
        ? responseUser.companiesVerifications.filter(
            (v: any) => v.company.id === activeCompanyId,
          )
        : responseUser.companiesVerifications;
      const pendingCount = calculatePendingVerifications(scoped);
      setPendingVerifications(pendingCount);
    } else {
      loadPendingVerifications(newUser);
    }

    loadTalentLMSData(newUser);
    permissionsCtx.initializePermissions();

    return newUser;
  };

  // ==================== AUTH ACTIONS ====================

  const completeLoginWithToken = async (): Promise<User> => {
    try {
      const token = authService.getStoredToken();
      if (!token) throw new Error('No authentication token found');
      const response = await authService.getCurrentUser(token);
      const userData = (response.data || response.user) as unknown as LoginResponseUser | undefined;
      if (!userData) throw new Error('Respuesta de usuario inválida');
      return await processLoginResponse(userData, token);
    } catch (error: unknown) {
      authService.removeToken();
      localStorage.removeItem('user');
      localStorage.removeItem('company');
      setUser(null);
      setCompany(null);
      setCompanyContext(null);
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);

      if ('requires2FA' in response && response.requires2FA) {
        const error = new Error('2FA_REQUIRED') as Error & {
          requires2FA: true;
          maskedEmail: string;
          expiresInMinutes?: number;
        };
        error.requires2FA = true;
        error.maskedEmail = response.email;
        if (response.expiresInMinutes !== undefined) {
          error.expiresInMinutes = response.expiresInMinutes;
        }
        throw error;
      }

      const directResponse = response as DirectLoginParsedResponse;
      const userData = directResponse.data ?? directResponse.user;
      if (!userData) throw new Error('No user data in login response');
      return await processLoginResponse(userData, directResponse.token);
    } catch (error: unknown) {
      if (error instanceof Error && error.message === '2FA_REQUIRED') throw error;
      authService.removeToken();
      localStorage.removeItem('user');
      localStorage.removeItem('company');
      setUser(null);
      setCompany(null);
      throw error;
    }
  };

  const triggerSessionExpired = useCallback(() => {
    if (user) setSessionExpired(true);
  }, [user]);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const logout = useCallback(() => {
    trackingService.trackAuthEvent('logout');
    setSessionExpired(false);
    setUserAvatarUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setUser(null);
    setCompany(null);
    setTalentLMSUser(null);
    setTalentLMSCourses(null);
    // Clear the API client's company-scoping header so subsequent requests
    // (e.g. from a fresh login) don't carry stale context.
    setCompanyContext(null);
    localStorage.removeItem('user');
    localStorage.removeItem('company');
    // Wipe everything in localStorage on logout except a small set of
    // device-scoped preferences (theme, locale, accepted-cookies banner).
    // Denylist-by-default avoids the previous bug where new per-user keys
    // had to be remembered by every author who added one — a missed entry
    // would leak that user's preference to the next user logging in on the
    // same browser. Keep this allowlist tight; if you find yourself adding
    // a new entry here, double-check it really is device-scoped (not
    // user-scoped).
    try {
      const DEVICE_SCOPED_KEEP = new Set<string>([
        'theme',          // light/dark preference
        'locale',         // language preference
        'cookies-accepted', // GDPR banner dismissal
        // 2FA "remember this device for 7 days" token. Bound server-side to
        // BOTH the device (User-Agent hash) and whichever account presents
        // it — a stale token from a previous user on a shared browser just
        // fails to match the next user's own twoFactorTrustedDevices, so
        // keeping it across logout can't leak trust to a different account.
        // Wiping it here was silently defeating "remember this device" on
        // every sign-out (manual or session-timeout, since both funnel
        // through this same logout()).
        'deviceTrustToken',
        // The tour-completion / tour-snooze maps are user-scoped INTERNALLY
        // ({ [userId]: true }), so wiping them on logout would re-show the
        // tour to a returning user — the exact bug that surfaced when the
        // same browser tab cycled between multiple accounts. Safe to keep
        // because a different user's _id is never a hit in another user's
        // entry, and a fresh first-time user still has no key for themselves.
        'dashboard-tour-completed',
      ]);
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key) continue;
        // 'token', 'user', 'company' are removed explicitly above; this
        // sweeps the rest. New code should namespace user-scoped data
        // under the `sy.user.*` convention so intent is obvious at a glance.
        if (!DEVICE_SCOPED_KEEP.has(key)) {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch { /* noop — storage access can throw in private mode */ }
    authService.removeToken();
    queryClient.clear();
    // Drop the apiClient in-memory response cache so a follow-up login on
    // the same tab cannot be served the previous user's cached GETs within
    // the CACHE_* TTL window. queryClient.clear() does not touch this map.
    clearApiClientCache();
  }, []);

  const hasRole = useCallback((role: UserRole | UserRole[]) => {
    if (!user) return false;
    if (Array.isArray(role)) return role.includes(user.role);
    return user.role === role;
  }, [user]);

  // Listen for session expired events
  useEffect(() => {
    const handleSessionExpired = () => triggerSessionExpired();
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [user, triggerSessionExpired]);

  // ==================== COMPOSE CONTEXT VALUE ====================

  const switchCompany = useCallback(async (newCompanyId: string) => {
    // Ask the BE to flip the active company and mint a fresh JWT — without
    // this the switch was purely client-side (localStorage only), so the
    // OLD token (still pointing at whichever company the user logged in
    // with) kept being what a page reload re-authenticated against via
    // GET /auth/me, silently reverting the switch every time.
    const result = await authService.switchCompany(newCompanyId);
    authService.setToken(result.token);

    setUser((prev) => {
      if (!prev) return prev;
      // `/auth/me` returns entries in two shapes depending on populate
      // mode: the typed FE shape uses `companyId`/`companyName`; the
      // populated BE shape uses `company: { _id, name }`. Match on
      // either while keeping reads typed where possible.
      const entry = (prev.companies || []).find((c) => {
        const populated = (c as unknown as { company?: { _id?: string } | string }).company;
        const id = (populated && typeof populated === 'object' ? populated._id : populated) || c.companyId;
        return String(id) === String(newCompanyId);
      });
      if (!entry) return prev;

      // `/auth/me`'s populate ("companies.company") includes companyType,
      // status, employeeCount, settings.modules — not just _id/name. Typing
      // this narrowly and picking only _id/name (as before) silently
      // dropped companyType off `user.company` right after a switch, which
      // broke every gate that reads `company?.companyType` off AuthContext
      // (useCompanyContext's canUseAuthCompany shortcut skips a network
      // refetch specifically because it trusts this field) — e.g. the
      // provider self-service gate defaulting to "open" because it
      // couldn't tell the switched-to company was a proveedor at all.
      const populatedCompany = (entry as unknown as {
        company?: {
          _id: string;
          name: string;
          companyType?: string;
          status?: string;
          employeeCount?: number;
          settings?: unknown;
        } | string;
      }).company;
      const companyObj = (populatedCompany && typeof populatedCompany === 'object') ? populatedCompany : null;
      const next: User = { ...prev };
      next.company = (companyObj
        ? { ...companyObj }
        : { _id: newCompanyId, name: entry.companyName }) as User['company'];
      next.companyId = newCompanyId;
      // Effective role follows the per-company entry. Platform users
      // (super_admin / safety_staff with globalRole) never enter this
      // path — they don't have multi-company switching at all.
      if (entry.role) next.role = entry.role;

      try { localStorage.setItem('user', JSON.stringify(toPersistableUser(next))); } catch { /* noop */ }

      // Mirror Company state with ONLY the fields we genuinely have
      // from the user's companies[] entry. Anything missing (industry,
      // contactPerson, legalRepresentative, etc.) is left undefined —
      // we fan out to the BE below to populate the full doc. Previous
      // implementation wrote placeholder defaults like
      // `industry: 'Construcción'` which downstream consumers would
      // read as truth until a refetch landed.
      if (companyObj) {
        const partial = {
          ...companyObj,
          _id: companyObj._id,
          id: companyObj._id,
          name: companyObj.name,
        } as Partial<Company> as Company;
        setCompany(partial);
        try { localStorage.setItem('company', JSON.stringify(toPersistableCompany(partial))); } catch { /* noop */ }
      } else {
        setCompany(null);
        try { localStorage.removeItem('company'); } catch { /* noop */ }
      }

      return next;
    });

    // Fires `company-context-changed` → AuthContext's existing listener
    // calls queryClient.clear(), so stale per-tenant cache entries die.
    setCompanyContext(newCompanyId);

    // Replace the partial Company immediately with the real document so
    // consumers reading off AuthContext.company (Layout chip, etc.)
    // don't see undefined fields for long. Failure here is non-fatal —
    // useCompany() callers will retry on their own.
    companyApi
      .getById(newCompanyId)
      .then((res: any) => {
        const fresh = res?.data || res;
        if (fresh?._id) {
          setCompany(fresh as Company);
          try { localStorage.setItem('company', JSON.stringify(toPersistableCompany(fresh as Company))); } catch { /* noop */ }
          // Also sync onto user.company — useCompanyContext's
          // canUseAuthCompany shortcut reads THIS, not the top-level
          // `company` state above, so leaving it stale would keep gates
          // like the provider self-service check guessing off partial data.
          setUser((prev) => {
            if (!prev || String(prev.companyId) !== String(newCompanyId)) return prev;
            const next: User = { ...prev, company: fresh as User['company'] };
            try { localStorage.setItem('user', JSON.stringify(toPersistableUser(next))); } catch { /* noop */ }
            return next;
          });
        }
      })
      .catch(() => { /* useCompany hooks will recover */ });
  }, []);

  const switchGlobalRole = useCallback(async (role: string) => {
    // Ask the BE to flip the active platform role and mint a fresh JWT.
    const result = await authService.switchGlobalRole(role);
    authService.setToken(result.token);

    setUser((prev) => {
      if (!prev) return prev;
      const next: User = {
        ...prev,
        role: result.role,
        globalRole: result.globalRole,
        globalRoles: result.globalRoles,
      } as User;
      try { localStorage.setItem('user', JSON.stringify(toPersistableUser(next))); } catch { /* noop */ }
      return next;
    });

    // The data scope differs per platform role (super_admin sees everything;
    // safety_staff sees only their assigned portfolio), and most query keys
    // don't discriminate on role — wipe the cache so lists refetch under the
    // new role instead of serving the previous role's cached data.
    queryClient.clear();
    try { clearApiClientCache(); } catch { /* noop */ }
  }, []);

  const updateUserLocal = useCallback((patch: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch } as User;
      // Keep the cached `name` synced when first/last changed so the
      // header avatar block updates without reload.
      if (patch.firstName != null || patch.lastName != null) {
        next.name = `${next.firstName || ''} ${next.lastName || ''}`.trim();
      }
      try { localStorage.setItem('user', JSON.stringify(toPersistableUser(next))); } catch { /* noop */ }
      return next;
    });
  }, []);

  const coreValues = useMemo(() => ({
    user, company, userAvatarUrl, login, completeLoginWithToken, logout,
    isAuthenticated: !!user, isLoading, hasRole,
    sessionExpired, triggerSessionExpired, clearSessionExpired,
    refreshUserAvatar, updateUserLocal, switchCompany, switchGlobalRole,
    pendingVerifications, refreshPendingVerifications,
    talentLMSUser, talentLMSCourses, refreshTalentLMSData,
    permissionsCtx,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    user, company, userAvatarUrl, isLoading, sessionExpired,
    pendingVerifications, talentLMSUser, talentLMSCourses,
    logout, hasRole, triggerSessionExpired, clearSessionExpired,
    refreshUserAvatar, updateUserLocal, switchCompany, switchGlobalRole,
    refreshPendingVerifications, refreshTalentLMSData,
    permissionsCtx,
  ]);

  const { permissionsCtx: pCtx, ...core } = coreValues;

  const contextValue = useMemo<AuthContextType>(() => ({
    ...core,
    permissions: pCtx.permissions,
    setPermissions: pCtx.setPermissions,
    showPermissionsCheck: pCtx.showPermissionsCheck,
    setShowPermissionsCheck: pCtx.setShowPermissionsCheck,
    savePermissionsToStorage: pCtx.savePermissionsToStorage,
  }), [core, pCtx]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== COMPOSED PROVIDER ====================

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider wraps all auth-related contexts.
 * Permissions is outermost (independent), then Auth core.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return (
    <PermissionsProvider>
      <AuthCoreProvider>
        {children}
      </AuthCoreProvider>
    </PermissionsProvider>
  );
};
